import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Gauge, Power, AlertTriangle, CheckCircle, Activity, Droplet, TrendingUp, TrendingDown, CircleDot, Lock, Unlock, Shield, Zap } from 'lucide-react';
import api from '../services/api';
import Breadcrumb from './Breadcrumb';
import { useAuth } from '../contexts/AuthContext';

const ValveControl = () => {
  const { user } = useAuth();
  const [valves, setValves] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedValves, setSelectedValves] = useState([]);
  const [toast, setToast] = useState(null);
  const [updatingValves, setUpdatingValves] = useState(new Set());

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [filter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (filter !== 'all') {
        params.status = filter;
      }
      
      const [valvesRes, statsRes] = await Promise.all([
        api.valves.getAll(params),
        api.valves.getStats()
      ]);
      
      setValves(valvesRes.data.data || []);
      setStats(statsRes.data.data);
    } catch (error) {
      console.error('Failed to fetch valve data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValveOperation = async (valveId, action) => {
    try {
      // Mark valve as updating
      setUpdatingValves(prev => new Set(prev).add(valveId));

      // Find the current valve to track old status
      const currentValve = valves.find(v => v.id === valveId);
      const oldStatus = currentValve?.status;

      // Normalize action to status (CLOSE -> CLOSED)
      const newStatus = action === 'CLOSE' ? 'CLOSED' : action;

      // Optimistic UI update - update the local state immediately
      setValves(prevValves => prevValves.map(v => {
        if (v.id === valveId) {
          const updatedValve = { ...v, status: newStatus };
          if (action === 'OPEN') updatedValve.opening_percentage = 100;
          else if (action === 'CLOSE') updatedValve.opening_percentage = 0;
          else if (action === 'PARTIAL') updatedValve.opening_percentage = 50;
          return updatedValve;
        }
        return v;
      }));

      // Update stats immediately with proper tracking
      updateStatsOptimistically(oldStatus, newStatus);

      // Make API call
      await api.valves.operate({
        valve_id: valveId,
        action: action,
        reason: `Minister control: ${action}`
      });

      // Show success feedback
      showSuccessToast(`✓ Valve ${action.toLowerCase()} command executed successfully`);
      
      // Fetch fresh data after a short delay to confirm server state
      setTimeout(() => {
        fetchData();
        setUpdatingValves(prev => {
          const newSet = new Set(prev);
          newSet.delete(valveId);
          return newSet;
        });
      }, 500);
    } catch (error) {
      console.error('Failed to operate valve:', error);
      showErrorToast('❌ Failed to operate valve. Please try again.');
      // Revert optimistic update by fetching fresh data
      fetchData();
      setUpdatingValves(prev => {
        const newSet = new Set(prev);
        newSet.delete(valveId);
        return newSet;
      });
    }
  };

  const handleBatchOperation = async (action) => {
    if (selectedValves.length === 0) {
      showErrorToast('⚠️ Please select valves first');
      return;
    }
    
    if (!confirm(`Confirm ${action} operation on ${selectedValves.length} selected valves?`)) {
      return;
    }

    try {
      // Normalize action to status (CLOSE -> CLOSED)
      const newStatus = action === 'CLOSE' ? 'CLOSED' : action;

      // Track old statuses for batch update
      const oldStatuses = valves
        .filter(v => selectedValves.includes(v.id))
        .map(v => v.status);

      // Optimistic update for all selected valves
      setValves(prevValves => prevValves.map(v => {
        if (selectedValves.includes(v.id)) {
          const updatedValve = { ...v, status: newStatus };
          if (action === 'OPEN') updatedValve.opening_percentage = 100;
          else if (action === 'CLOSE') updatedValve.opening_percentage = 0;
          else if (action === 'PARTIAL') updatedValve.opening_percentage = 50;
          return updatedValve;
        }
        return v;
      }));

      // Update stats for batch with proper tracking
      oldStatuses.forEach(oldStatus => {
        updateStatsOptimistically(oldStatus, newStatus);
      });

      await api.valves.batchOperate(selectedValves, action, `Batch ${action} operation`);
      showSuccessToast(`✓ Batch ${action.toLowerCase()} operation completed on ${selectedValves.length} valves`);
      setSelectedValves([]);
      
      // Fetch fresh data after delay
      setTimeout(() => fetchData(), 500);
    } catch (error) {
      console.error('Failed to batch operate:', error);
      showErrorToast('❌ Batch operation failed. Please try again.');
      fetchData();
    }
  };

  const toggleValveSelection = (valveId) => {
    setSelectedValves(prev => 
      prev.includes(valveId) 
        ? prev.filter(id => id !== valveId)
        : [...prev, valveId]
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN': return 'text-green-400 border-green-500/50 bg-green-500/20';
      case 'CLOSED': return 'text-red-400 border-red-500/50 bg-red-500/20';
      case 'PARTIAL': return 'text-orange-400 border-orange-500/50 bg-orange-500/20';
      default: return 'text-gray-400 border-gray-500/50 bg-gray-500/20';
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'Healthy': return 'text-green-400';
      case 'Warning': return 'text-yellow-400';
      case 'Critical': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const canOperate = () => {
    return user && ['Minister', 'DeptHead'].includes(user.role);
  };

  // Helper function to update stats optimistically
  const updateStatsOptimistically = (oldStatus, newStatus) => {
    if (!stats) return;
    
    setStats(prevStats => {
      const updated = { ...prevStats };
      
      // Decrement old status count
      if (oldStatus === 'OPEN') {
        updated.open = Math.max(0, (updated.open || 0) - 1);
      } else if (oldStatus === 'CLOSED') {
        updated.closed = Math.max(0, (updated.closed || 0) - 1);
      } else if (oldStatus === 'PARTIAL') {
        updated.partial = Math.max(0, (updated.partial || 0) - 1);
      }
      
      // Increment new status count
      if (newStatus === 'OPEN') {
        updated.open = (updated.open || 0) + 1;
      } else if (newStatus === 'CLOSED') {
        updated.closed = (updated.closed || 0) + 1;
      } else if (newStatus === 'PARTIAL') {
        updated.partial = (updated.partial || 0) + 1;
      }
      
      return updated;
    });
  };

  // Toast notification helpers
  const showSuccessToast = (message) => {
    setToast({ type: 'success', message });
    setTimeout(() => setToast(null), 3000);
  };

  const showErrorToast = (message) => {
    setToast({ type: 'error', message });
    setTimeout(() => setToast(null), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto mb-6"></div>
            <div className="absolute inset-0 w-20 h-20 mx-auto">
              <Gauge className="w-8 h-8 text-cyan-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
            </div>
          </div>
          <p className="text-cyan-400 font-semibold tracking-wide">INITIALIZING VALVE CONTROL...</p>
          <p className="text-gray-500 text-sm mt-2">Loading valve network</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-24 right-6 z-50 px-6 py-4 rounded-lg shadow-2xl border-2 animate-in slide-in-from-top-5 ${
          toast.type === 'success' 
            ? 'bg-green-900/90 border-green-500/50 text-green-100' 
            : 'bg-red-900/90 border-red-500/50 text-red-100'
        }`}>
          <div className="flex items-center gap-3">
            {toast.type === 'success' ? (
              <CheckCircle className="text-green-400" size={20} />
            ) : (
              <AlertTriangle className="text-red-400" size={20} />
            )}
            <p className="font-semibold">{toast.message}</p>
          </div>
        </div>
      )}

      <Breadcrumb items={[{ label: 'Valve Control Dashboard' }]} onNavigate={() => {}} />
      
      {/* AR/VR Style Header */}
      <div className="relative rounded-xl overflow-hidden border border-cyan-500/30 bg-gradient-to-br from-[#12172e] via-[#0f1629] to-[#12172e] p-6 shadow-2xl">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(0deg, rgba(0, 217, 255, 0.1) 0px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, rgba(0, 217, 255, 0.1) 0px, transparent 1px, transparent 40px)',
          }}></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 tracking-wide flex items-center gap-3">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                  VALVE CONTROL CENTER
                </span>
                <Shield className="text-cyan-400" size={32} />
              </h1>
              <p className="text-gray-400 text-sm md:text-base">AR/VR Enhanced Control System • Real-time Operations</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse"></div>
                <span className="text-cyan-400 font-semibold text-sm">REMOTE CONTROL ACTIVE</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <Card className="bg-[#12172e] border-cyan-500/30 text-white hover:border-cyan-500/50 transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 font-medium tracking-wide">TOTAL VALVES</p>
                  <p className="text-4xl font-bold text-cyan-400 mt-2 transition-all duration-500">{stats.total}</p>
                </div>
                <Gauge className="text-cyan-400" size={32} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#12172e] border-green-500/30 text-white hover:border-green-500/50 transition-all duration-300 hover:scale-105">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 font-medium tracking-wide">OPEN</p>
                  <p className="text-4xl font-bold text-green-400 mt-2 transition-all duration-500">{stats.open}</p>
                </div>
                <Unlock className="text-green-400" size={32} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#12172e] border-red-500/30 text-white hover:border-red-500/50 transition-all duration-300 hover:scale-105">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 font-medium tracking-wide">CLOSED</p>
                  <p className="text-4xl font-bold text-red-400 mt-2 transition-all duration-500">{stats.closed}</p>
                </div>
                <Lock className="text-red-400" size={32} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#12172e] border-orange-500/30 text-white hover:border-orange-500/50 transition-all duration-300 hover:scale-105">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 font-medium tracking-wide">PARTIAL</p>
                  <p className="text-4xl font-bold text-orange-400 mt-2 transition-all duration-500">{stats.partial}</p>
                </div>
                <Activity className="text-orange-400" size={32} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#12172e] border-yellow-500/30 text-white hover:border-yellow-500/50 transition-all duration-300 hover:scale-105">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 font-medium tracking-wide">CRITICAL</p>
                  <p className="text-4xl font-bold text-yellow-400 mt-2 transition-all duration-500">{stats.critical_health}</p>
                </div>
                <AlertTriangle className="text-yellow-400" size={32} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter & Batch Controls */}
      <Card className="bg-[#12172e] border-cyan-500/30 text-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-400 font-medium tracking-wide">FILTER:</span>
              {['all', 'OPEN', 'CLOSED', 'PARTIAL'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    filter === status
                      ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
                      : 'bg-slate-800/50 text-gray-400 border border-slate-700/50 hover:border-cyan-500/30'
                  }`}
                >
                  {status === 'all' ? 'ALL' : status}
                </button>
              ))}
            </div>

            {canOperate() && selectedValves.length > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-400">{selectedValves.length} selected</span>
                <Button
                  onClick={() => handleBatchOperation('OPEN')}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 border-0 text-white font-semibold"
                >
                  <Unlock size={16} className="mr-2" />
                  OPEN ALL
                </Button>
                <Button
                  onClick={() => handleBatchOperation('CLOSE')}
                  className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 border-0 text-white font-semibold"
                >
                  <Lock size={16} className="mr-2" />
                  CLOSE ALL
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Valve Grid - AR/VR Style */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {valves.map((valve) => (
          <Card 
            key={valve.id} 
            className={`bg-[#12172e] border-cyan-500/30 text-white hover:border-cyan-500/50 transition-all duration-300 relative overflow-hidden ${
              selectedValves.includes(valve.id) ? 'ring-2 ring-cyan-500' : ''
            } ${
              updatingValves.has(valve.id) ? 'animate-pulse ring-2 ring-yellow-500' : ''
            }`}
          >
            {/* Scan line effect */}
            <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-50"></div>
            
            <CardContent className="p-6">
              {/* Valve Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white tracking-wide mb-1">{valve.name}</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 bg-slate-800/50 px-2 py-1 rounded">{valve.valve_type}</span>
                    <span className="text-xs text-gray-400 bg-slate-800/50 px-2 py-1 rounded">{valve.dma_zone}</span>
                    {valve.is_automated && <Zap size={12} className="text-yellow-400" title="Automated" />}
                  </div>
                </div>
                {canOperate() && (
                  <input
                    type="checkbox"
                    checked={selectedValves.includes(valve.id)}
                    onChange={() => toggleValveSelection(valve.id)}
                    className="w-5 h-5 rounded border-cyan-500/50 bg-slate-900 text-cyan-500 focus:ring-cyan-500"
                  />
                )}
              </div>

              {/* Circular Valve Visualization */}
              <div className="relative w-40 h-40 mx-auto mb-4">
                {/* Updating indicator */}
                {updatingValves.has(valve.id) && (
                  <div className="absolute -top-2 -right-2 z-20">
                    <div className="w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center animate-spin">
                      <Activity size={14} className="text-white" />
                    </div>
                  </div>
                )}
                
                {/* Outer ring */}
                <div className={`absolute inset-0 rounded-full border-4 transition-all duration-500 ${getStatusColor(valve.status)}`}>
                  {/* Inner circle */}
                  <div className="absolute inset-4 rounded-full bg-slate-900/80 flex items-center justify-center transition-all duration-500">
                    <div className="text-center">
                      <p className={`text-3xl font-bold transition-all duration-500 ${getStatusColor(valve.status).split(' ')[0]}`}>
                        {valve.opening_percentage}%
                      </p>
                      <p className={`text-xs font-medium transition-all duration-500 ${getStatusColor(valve.status).split(' ')[0]} mt-1`}>
                        {valve.status}
                      </p>
                    </div>
                  </div>
                  
                  {/* Rotating indicator for open valves */}
                  {valve.status === 'OPEN' && (
                    <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
                      <div className="w-2 h-2 bg-green-400 rounded-full absolute top-0 left-1/2 -ml-1 shadow-lg shadow-green-400/50"></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp size={14} className="text-cyan-400" />
                    <p className="text-xs text-gray-400">Upstream</p>
                  </div>
                  <p className="text-lg font-semibold text-cyan-400">{valve.pressure_upstream} Bar</p>
                </div>
                <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingDown size={14} className="text-teal-400" />
                    <p className="text-xs text-gray-400">Downstream</p>
                  </div>
                  <p className="text-lg font-semibold text-teal-400">{valve.pressure_downstream} Bar</p>
                </div>
              </div>

              {/* Flow Rate */}
              <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Droplet size={14} className="text-blue-400" />
                    <p className="text-xs text-gray-400">Flow Rate</p>
                  </div>
                  <p className="text-lg font-semibold text-blue-400">{valve.flow_rate} KL/min</p>
                </div>
              </div>

              {/* Health Status */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-400">Health Status:</span>
                <span className={`text-sm font-semibold ${getHealthColor(valve.health_status)} flex items-center gap-1`}>
                  {valve.health_status === 'Healthy' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
                  {valve.health_status}
                </span>
              </div>

              {/* Controls */}
              {canOperate() && valve.is_remote_controllable && (
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    onClick={() => handleValveOperation(valve.id, 'OPEN')}
                    disabled={valve.status === 'OPEN'}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 disabled:opacity-30 border-0 text-white font-semibold"
                  >
                    <Unlock size={14} />
                  </Button>
                  <Button
                    onClick={() => handleValveOperation(valve.id, 'PARTIAL')}
                    className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 border-0 text-white font-semibold"
                  >
                    <Activity size={14} />
                  </Button>
                  <Button
                    onClick={() => handleValveOperation(valve.id, 'CLOSE')}
                    disabled={valve.status === 'CLOSED'}
                    className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 disabled:opacity-30 border-0 text-white font-semibold"
                  >
                    <Lock size={14} />
                  </Button>
                </div>
              )}

              {/* ILK Link */}
              {valve.linked_ilk_id && (
                <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded flex items-center gap-2">
                  <AlertTriangle size={14} className="text-red-400" />
                  <span className="text-xs text-red-400 font-medium">Linked to ILK Detection</span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ValveControl;
